import { useEffect, useRef, useState } from 'react';
import { Building2, ImageUp, Trash2 } from 'lucide-react';
import { toast } from '../../store/toast.js';
import {
  useCompanies, useDeleteCompanyLogo, useUpdateCompany, useUploadCompanyLogo,
  type CompanyDTO, type CompanyPatch,
} from '../../api/hooks.js';

// Sociétés — l'identité légale de chaque entité : logo, raison sociale,
// adresse, NIF/NIS/AI/CNAS et RIB. Ces champs sont estampillés sur tous
// les documents générés (attestation de travail, fiche de salaire,
// grille mensuelle, proposition d'embauche). Un champ laissé vide
// disparaît simplement du document — rien n'est obligatoire.

// Ordre d'affichage = ordre d'impression dans le pied de page des PDF.
type Field = keyof CompanyPatch;
const FIELDS: Array<{ key: Field; label: string; help: string; placeholder: string; max: number; mono?: boolean }> = [
  { key: 'legalName', label: 'Raison sociale', placeholder: 'SARL SHIFTIN', max: 120,
    help: 'Nom légal complet, tel qu’il doit apparaître en tête des documents.' },
  { key: 'nif', label: 'NIF', placeholder: '000000000000000', max: 40, mono: true,
    help: 'Numéro d’identification fiscale.' },
  { key: 'nis', label: 'NIS', placeholder: '000000000000000', max: 40, mono: true,
    help: 'Numéro d’identification statistique.' },
  { key: 'ai', label: 'Article d’imposition', placeholder: '00000000000', max: 40, mono: true,
    help: 'Numéro d’article d’imposition (AI).' },
  { key: 'cnas', label: 'N° employeur CNAS', placeholder: '000000000', max: 40, mono: true,
    help: 'Immatriculation employeur à la CNAS.' },
  { key: 'rib', label: 'RIB bancaire', placeholder: '000 00000 0000000000 00', max: 40, mono: true,
    help: 'Compte bancaire de la société, imprimé en pied de document.' },
];

const LOGO_MAX_BYTES = 512 * 1024;
const LOGO_TYPES = ['image/png', 'image/jpeg', 'image/jpg'];

interface Props { ping: () => void; }

export function CompaniesCard({ ping }: Props) {
  const companies = useCompanies();

  if (companies.isLoading) {
    return <div className="scard-body"><p className="srow-help" style={{ padding: 16 }}>Chargement…</p></div>;
  }
  const rows = companies.data ?? [];
  if (rows.length === 0) {
    return (
      <div className="scard-body">
        <div className="empty">
          <div className="empty-ico"><Building2 /></div>
          <div className="empty-title">Aucune société accessible</div>
          <div className="empty-sub">Votre compte n’a la portée d’aucune entité.</div>
        </div>
      </div>
    );
  }

  return (
    <div className="scard-body">
      <p className="srow-help" style={{ padding: '2px 6px 14px' }}>
        Ces informations sont reprises telles quelles sur l’attestation de travail, la fiche de
        salaire, la grille mensuelle et la proposition d’embauche. Un champ vide n’apparaît pas
        sur le document.
      </p>
      <div className="co-list">
        {rows.map((c) => <CompanyBlock key={c.id} company={c} ping={ping} />)}
      </div>
    </div>
  );
}

function CompanyBlock({ company, ping }: { company: CompanyDTO; ping: () => void }) {
  const update = useUpdateCompany();
  return (
    <section className="co-block" style={{ '--co': company.color } as React.CSSProperties}
      data-testid={`company-${company.id}`}>
      <header className="co-head">
        <LogoWell company={company} ping={ping} />
        <div className="co-ident">
          <div className="co-name">{company.effectiveLegalName}</div>
          <div className="co-meta">
            <span className="co-badge">{company.short}</span>
            <span>{company.name}</span>
          </div>
        </div>
      </header>

      <div className="set-rows">
        <div className="srow">
          <div>
            <div className="srow-label">Adresse du siège</div>
            <div className="srow-help">
              Une ligne par élément ; le document la rend sur une seule ligne, séparée par des virgules.
            </div>
          </div>
          <TextField company={company} field="address" label="Adresse du siège" multiline max={400}
            placeholder={'Cité …, Lot n° …\nAlger, Algérie'} update={update} ping={ping} />
        </div>
        {FIELDS.map((f) => (
          <div className="srow" key={f.key}>
            <div>
              <div className="srow-label">{f.label}</div>
              <div className="srow-help">{f.help}</div>
            </div>
            <TextField company={company} field={f.key} label={f.label} max={f.max} mono={f.mono}
              placeholder={f.placeholder} update={update} ping={ping} />
          </div>
        ))}
      </div>
    </section>
  );
}

// Enregistrement au blur (comme le reste de Paramètres) : un PUT par
// champ réellement modifié, no-op sinon.
function TextField({
  company, field, label, max, mono, multiline, placeholder, update, ping,
}: {
  company: CompanyDTO;
  field: Field;
  label: string;
  max: number;
  mono?: boolean;
  multiline?: boolean;
  placeholder: string;
  update: ReturnType<typeof useUpdateCompany>;
  ping: () => void;
}) {
  const current = company[field] ?? '';
  const [value, setValue] = useState(current);
  useEffect(() => { setValue(current); }, [current]);

  const commit = () => {
    const v = value.trim();
    if (v === current.trim()) { setValue(current); return; }
    update.mutate({ id: company.id, patch: { [field]: v } }, {
      onSuccess: ping,
      onError: () => { toast.danger('Échec de l’enregistrement.'); setValue(current); },
    });
  };

  const testId = `company-${company.id}-${field}`;
  if (multiline) {
    return (
      <textarea className="finput co-textarea" value={value} maxLength={max} rows={2}
        placeholder={placeholder}
        onChange={(e) => setValue(e.target.value)} onBlur={commit}
        data-testid={testId} aria-label={`${label} — ${company.name}`} />
    );
  }
  return (
    <input className={'finput' + (mono ? ' mono' : '')} value={value} maxLength={max}
      placeholder={placeholder}
      onChange={(e) => setValue(e.target.value)} onBlur={commit}
      onKeyDown={(e) => { if (e.key === 'Enter') (e.target as HTMLInputElement).blur(); }}
      data-testid={testId} aria-label={`${label} — ${company.name}`} />
  );
}

function LogoWell({ company, ping }: { company: CompanyDTO; ping: () => void }) {
  const uploadLogo = useUploadCompanyLogo();
  const deleteLogo = useDeleteCompanyLogo();
  const inputRef = useRef<HTMLInputElement | null>(null);

  const pick = (file: File | undefined) => {
    if (!file) return;
    // Mêmes bornes que la route : PNG/JPEG (les seuls formats que le
    // générateur PDF sait embarquer) et 512 Ko.
    if (!LOGO_TYPES.includes(file.type)) { toast.danger('Format non supporté — PNG ou JPEG uniquement.'); return; }
    if (file.size > LOGO_MAX_BYTES) { toast.danger('Logo trop lourd — 512 Ko maximum.'); return; }
    uploadLogo.mutate({ id: company.id, file }, {
      onSuccess: ping,
      onError: () => toast.danger('Échec de l’envoi du logo.'),
    });
  };

  return (
    <div className="co-logo">
      <div className="co-logo-well" data-testid={`company-${company.id}-logo`}>
        {company.logo
          ? <img src={company.logo} alt={`Logo ${company.name}`} />
          : <span className="co-logo-fallback">{company.short}</span>}
      </div>
      <div className="co-logo-actions">
        <input ref={inputRef} type="file" accept="image/png,image/jpeg" hidden
          onChange={(e) => { pick(e.target.files?.[0]); e.target.value = ''; }}
          data-testid={`company-${company.id}-logo-input`} aria-label={`Logo ${company.name}`} />
        <button className="btn btn-sm" onClick={() => inputRef.current?.click()}
          disabled={uploadLogo.isPending} data-testid={`company-${company.id}-logo-upload`}>
          <ImageUp /> {company.logo ? 'Remplacer' : 'Ajouter un logo'}
        </button>
        {company.logo && (
          <button className="btn btn-sm btn-ghost" data-testid={`company-${company.id}-logo-delete`}
            disabled={deleteLogo.isPending}
            onClick={() => deleteLogo.mutate(company.id, {
              onSuccess: ping,
              onError: () => toast.danger('Échec de la suppression.'),
            })}>
            <Trash2 /> Retirer
          </button>
        )}
        <span className="co-logo-hint">PNG ou JPEG · 512 Ko max</span>
      </div>
    </div>
  );
}
